import type { ColorMode } from '@chakra-ui/react';
import type { Size, ChakraStylesConfig } from 'chakra-react-select';

import type { Option } from './types';

// import theme from 'theme/theme';
// import getFormStyles from 'theme/utils/getFormStyles';

function getValueContainerStyles(size?: Size) {
  switch (size) {
    case 'sm':
    case 'md': {
      return {
        paddingLeft: 4,
      };
    }
    case 'lg': {
      return {
        paddingLeft: 6,
      };
    }
    default: {
      return {};
    }
  }
}

function getSingleValueStyles(size?: Size) {
  switch (size) {
    case 'sm':
    case 'md': {
      return {
        top: '26px',
      };
    }
    case 'lg': {
      return {
        top: '38px',
      };
    }
    default: {
      return {};
    }
  }
}

const DARK_SELECT_BG = '#111111';
const DARK_SELECT_FG = '#FFFFFF';
const DARK_SELECT_BORDER = '#222222';

const getChakraStyles: (colorMode: ColorMode) => ChakraStylesConfig<Option> = (colorMode) => {
  const isLight = colorMode === 'light';

  const controlBg = `${ isLight ? '#FFFFFF' : DARK_SELECT_BG } !important`;
  const controlFg = `${ isLight ? '#000000' : DARK_SELECT_FG } !important`;
  const inputFg = `${ isLight ? '#000000' : DARK_SELECT_FG } !important`;
  const singleFg = `${ isLight ? '#000000' : DARK_SELECT_FG } !important`;
  const menuBg = `${ isLight ? '#FFFFFF' : DARK_SELECT_BG } !important`;

  return {
    control: (provided, state) => {
      const defaultBorder = isLight ? '#E9ECEF' : DARK_SELECT_BORDER;
      const borderColor = state.isFocused ? '#14DCAC' : defaultBorder;

      return {
        ...provided,
        border: '1px solid',
        borderColor,
        boxShadow: state.isFocused ? '0 0 0 1px #14DCAC' : 'none',
        bgColor: controlBg,
        color: controlFg,
        minHeight: '48px !important',
        cursor: 'pointer',
        '&:hover': {
          borderColor: '#14DCAC',
          ...(isLight ? {} : {
            bgColor: `${ DARK_SELECT_BG } !important`,
            color: `${ DARK_SELECT_FG } !important`,
          }),
        },
      };
    },
    inputContainer: (provided) => ({
      ...provided,
      py: 0,
      mx: 0,
      input: {
        height: 'auto !important',
        padding: '12px 16px !important',
        color: inputFg,
        '&:focus': {
          borderColor: '#14DCAC !important',
        },
      },
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      ...getValueContainerStyles(state.selectProps.size),
      py: 0,
      pl: '12px',
      '& > div': {
        color: isLight ? '#999999' : 'gray.100',
      },
    }),
    singleValue: (provided, state) => ({
      ...provided,
      mx: 0,
      transform: 'none',
      ...getSingleValueStyles(state.selectProps.size),
      top: 'auto',
      color: singleFg,
    }),

    menuList: (provided) => ({
      ...provided,
      bg: menuBg,
      padding: 0,
      maxHeight: '300px',
      border: isLight ? '1px solid #E9ECEF' : `1px solid ${ DARK_SELECT_BORDER }`,
      borderRadius: '8px',
    }),

    option: (provided, state) => {
      let rowBg = 'transparent';
      if (state.isSelected || state.isFocused) {
        rowBg = isLight ? '#F0F0F0' : '#2A2A2A';
      }
      const rowColor = isLight ? '#000000' : DARK_SELECT_FG;
      const rowHoverBg = isLight ? '#F0F0F0' : '#2A2A2A';

      return {
        ...provided,
        bg: rowBg,
        color: rowColor,
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer',
        '&:hover': {
          bg: rowHoverBg,
          color: rowColor,
        },
      };
    },
  };
};

export { getChakraStyles };
